/**
 * ContentLoader — JSON config loader for game content
 *
 * Loads and indexes enemy definitions, weapon definitions,
 * and upgrade definitions from JSON files. Provides weighted
 * random selection for spawning and upgrade picks.
 */

export class ContentLoader {
  constructor() {
    this.enemies = [];
    this.weapons = [];
    this.upgrades = [];

    this._enemyWeightTotal = 0;
    this._upgradesByRarity = {};
  }

  loadEnemies(data) {
    this.enemies = data;
    this._enemyWeightTotal = data.reduce((sum, e) => sum + (e.spawnWeight || 10), 0);
  }

  loadWeapons(data) {
    this.weapons = data;
  }

  loadUpgrades(data) {
    this.upgrades = data;
    this._upgradesByRarity = {};
    for (const u of data) {
      const r = u.rarity || 'common';
      if (!this._upgradesByRarity[r]) this._upgradesByRarity[r] = [];
      this._upgradesByRarity[r].push(u);
    }
  }

  // --- Weighted Random Selection ---

  /** Pick a random enemy type based on spawn weights */
  randomEnemy() {
    let roll = Math.random() * this._enemyWeightTotal;
    for (const e of this.enemies) {
      roll -= (e.spawnWeight || 10);
      if (roll <= 0) return e;
    }
    return this.enemies[0];
  }

  /** Pick N random upgrades with rarity weighting */
  randomUpgrades(count = 3, excludeIds = []) {
    const pool = this.upgrades.filter(u => !excludeIds.includes(u.id));
    if (pool.length <= count) return [...pool];

    // Rarity weights: common=50, uncommon=30, rare=15, legendary=5
    const rarityWeight = { common: 50, uncommon: 30, rare: 15, legendary: 5 };
    const totalWeight = pool.reduce((sum, u) => sum + (rarityWeight[u.rarity] || 10), 0);

    const picks = [];
    const used = new Set();

    while (picks.length < count && picks.length < pool.length) {
      let roll = Math.random() * totalWeight;
      for (let i = 0; i < pool.length; i++) {
        if (used.has(i)) continue;
        roll -= (rarityWeight[pool[i].rarity] || 10);
        if (roll <= 0) {
          picks.push(pool[i]);
          used.add(i);
          break;
        }
      }
      // Fallback if roll didn't pick (floating point edge case)
      if (picks.length < count && used.size < pool.length) {
        for (let i = 0; i < pool.length; i++) {
          if (!used.has(i)) {
            picks.push(pool[i]);
            used.add(i);
            break;
          }
        }
      }
    }

    return picks;
  }

  /** Get enemy definition by ID */
  getEnemy(id) {
    return this.enemies.find(e => e.id === id);
  }

  /** Get weapon definition by ID */
  getWeapon(id) {
    return this.weapons.find(w => w.id === id);
  }

  /** Get upgrade definition by ID */
  getUpgrade(id) {
    return this.upgrades.find(u => u.id === id);
  }
}
